/**
 * Wraps a function to guard against excessive recursion depth.
 * Each wrapped function maintains its own depth counter.
 *
 * @param fn - The function to wrap
 * @param max - Maximum recursion depth allowed
 * @param defaultValue - Value to return for the final call when maximum depth is exceeded
 * @returns The wrapped function with depth guarding
 */
export function maxDepthRecursionWrapper<T extends (...args: unknown[]) => unknown>(
  fn: T,
  max: number,
  defaultValue: ReturnType<T>,
): T {
  let depth = 0;

  return function (...args: Parameters<T>) {
    depth += 1;

    if (depth > max) {
      depth -= 1;
      return defaultValue;
    }

    try {
      const result = fn(...args);
      return result;
    } finally {
      depth -= 1;
    }
  } as T;
}
